import { Component, Input, OnInit } from '@angular/core';
import { TCenter } from '@shared/interfaces/TCenter';
import { TProject, TRegionBrick } from '@shared/interfaces/TProject';
import { TUser } from '@shared/interfaces/TUser';
import { Observable, of, switchMap, zip } from 'rxjs';
import { momentumLongDesc, momentumShortDesc } from 'src/app/constants/momentum-description.constant';
import { addSmart } from 'src/app/helpers/add-smart.helper';
import { BrickSalesSummary } from 'src/app/interfaces/brick-sales-summary.interface';
import { Region } from 'src/app/interfaces/region.interface';
import { SalesMomentum, SalesMomentumCenterSmall } from 'src/app/interfaces/sales-momentum.interface';
import { TopAndBottomX } from 'src/app/interfaces/top-and-bottom-x.interface';
import { AuthService } from 'src/app/services/auth.service';
import { KamReportsService } from '../../services/kam-reports.service';

@Component({
  selector: 'app-sales-momentum',
  templateUrl: './sales-momentum.component.html',
  styleUrls: ['./sales-momentum.component.scss']
})
export class SalesMomentumComponent implements OnInit {

  @Input() project: TProject;
  @Input() rowsToShow: number;
  @Input() showTerritory: boolean = false;

  private regions: Region[];

  public salesMomentum$: Observable<SalesMomentum>;
  public showBricks: boolean = true;
  public showAccounts: boolean = true;

  public momentumLongDesc: string = momentumLongDesc;
  public momentumShortDesc: string = momentumShortDesc;

  constructor(
    private _kamReportsService: KamReportsService,
    private _authService: AuthService,
  ) {}

  get centers$(): Observable<TCenter[]> { return this._kamReportsService.allCenters$; }
  get selectedRegions$(): Observable<Region[]> { return this._kamReportsService.selectedRegions$; }

  ngOnInit(): void {
    this.salesMomentum$ = this.buildSalesMomentum$(this.project, this.centers$, this.selectedRegions$);
    this.regions = this._kamReportsService.getRegions(this._authService.getCurrentUser() as TUser, this.project);
  }

      /**
   * Build data for Sales momentum table
   * @param  {TProject} project
   * @param  {TCenter[]} centers
   * @returns SalesMomentum
   */
  buildSalesMomentum$(project: TProject, centers$: Observable<TCenter[]>, selectedRegions$: Observable<Region[]>): Observable<SalesMomentum> {
    // bricks tables
    let bricks: TRegionBrick[] = project.accountBricks;
    return zip(centers$, selectedRegions$).pipe(
      switchMap(([centers, selectedRegions]) => {
        this.showBricks = (bricks && bricks.length > 0) ? true : false;
        this.showAccounts = (centers && centers.length > 0) ? true : false;
        let result: SalesMomentum = { brickPlus: [], brickMinus: [], accountPlus: [], accountMinus: [] };
        const filteredBricks: TRegionBrick[] = this._kamReportsService.filterBricksBySelectedRegions(bricks, selectedRegions);
        let bricksSalesSummary: BrickSalesSummary[] = this._kamReportsService.getBricksWithLastXMonthsSales(filteredBricks, 1);
        // console.log('BRICKS SALES SUMMARY', bricksSalesSummary);

        // console.log('BEFORE SORT', bricksSalesSummary.map(bss => Math.round(+bss.salesMomentumShort)).join(', '), bricksSalesSummary);
        // bricksSalesSummary.sort((a, b) => {
        //   let aSorter = a.salesMomentumShort || 0;
        //   let bSorter = b.salesMomentumShort || 0;
        //   if (aSorter === '0%') aSorter = 0;
        //   if (bSorter === '0%') bSorter = 0;
        //   if (aSorter === 'N/A') aSorter = Number.NEGATIVE_INFINITY;
        //   if (bSorter === 'N/A') bSorter = Number.NEGATIVE_INFINITY;
        //   aSorter = this.getNumber(aSorter);
        //   bSorter = this.getNumber(bSorter);

        //   if (a.salesMomentumShort && b.salesMomentumShort) {
        //     if (aSorter > bSorter) {
        //       return -1;
        //     } else if (aSorter < bSorter) {
        //       return 1;
        //     }
        //   }
        //   return 0;
        // });
        // console.log('AFTER SORT', bricksSalesSummary.map(bss => Math.round(+bss.salesMomentumShort)).join(', '), bricksSalesSummary);

        // const brickTbX: TopAndBottomX<BrickSalesSummary> = this._kamReportsService.getTopAndBottomX(bricksSalesSummary, this.rowsToShow);
        const brickTbX: TopAndBottomX<BrickSalesSummary> = this.splitBricks<BrickSalesSummary>(
          bricksSalesSummary,
          'salesMomentumShort',
          'salesMomentumLong',
          'salesTotal',
          'salesTotal',
          { name: 'N/A', regionId: 'N/A', salesTotal: 'N/A', salesMomentumShort: 'N/A', salesMomentumLong: 'N/A' }
        );

        brickTbX.topX.forEach(brick => {
          brick.salesTotal = isNaN(+brick.salesTotal) ? 'N/A' : Math.round(+brick.salesTotal);
          brick.salesMomentumShort = isNaN(+brick.salesMomentumShort) ? 'N/A' : Math.round(+brick.salesMomentumShort) + '%';
          brick.salesMomentumLong = isNaN(+brick.salesMomentumLong) ? 'N/A' : Math.round(+brick.salesMomentumLong) + '%';
        });
        brickTbX.btmX.forEach(brick => {
          brick.salesTotal = isNaN(+brick.salesTotal) ? 'N/A' : Math.round(+brick.salesTotal);
          brick.salesMomentumShort = isNaN(+brick.salesMomentumShort) ? 'N/A' : Math.round(+brick.salesMomentumShort) + '%';
          brick.salesMomentumLong = isNaN(+brick.salesMomentumLong) ? 'N/A' : Math.round(+brick.salesMomentumLong) + '%';
        });
        // build result
        result = {
          ...result,
          brickPlus: brickTbX.topX.map(topX => ({ brick: topX.name, region: this.getRegionName(topX.regionId), total: topX.salesTotal, fourMonth: topX.salesMomentumShort, py: topX.salesMomentumLong })),
          brickMinus: brickTbX.btmX.map(btmX => ({ brick: btmX.name, region: this.getRegionName(btmX.regionId), total: btmX.salesTotal, fourMonth: btmX.salesMomentumShort, py: btmX.salesMomentumLong })),
        };



        // centers tables
        const centersSmall: SalesMomentumCenterSmall[] = [];
        centers.forEach(center => {
          const projectData = center.projectsData.filter(item => item.projectId === project.id)[0];
          const smallCenter = {
            id: center.c_id,
            name: center.c_name,
            regionId: projectData.c_project_account_region,
            salesLatestPeriod: Math.round(+projectData.sales_latest_period),
            salesMomentumShort: Math.round(+projectData.sales_momentum_short),
            salesMomentumLong: Math.round(+projectData.sales_momentum_long),
          };
          centersSmall.push(smallCenter);
        });
        centersSmall.sort((a: SalesMomentumCenterSmall, b: SalesMomentumCenterSmall) => {
          let aSorter = a.salesMomentumShort || 0;
          let bSorter = b.salesMomentumShort || 0;
          if (aSorter === '0%') aSorter = 0;
          if (bSorter === '0%') bSorter = 0;
          if (aSorter === 'N/A') aSorter = Number.NEGATIVE_INFINITY;
          if (bSorter === 'N/A') bSorter = Number.NEGATIVE_INFINITY;

          if (a.salesMomentumShort && b.salesMomentumShort) {
            if (aSorter > bSorter) {
              return -1;
            } else if (aSorter < bSorter) {
              return 1;
            }
          }
          return 0;
        });

        // const centerTbX: TopAndBottomX<SalesMomentumCenterSmall> = this._kamReportsService.getTopAndBottomX(centersSmall, this.rowsToShow);
        const centerTbX: TopAndBottomX<SalesMomentumCenterSmall> = this.splitBricks<SalesMomentumCenterSmall>(
          centersSmall,
          'salesMomentumShort',
          'salesMomentumLong',
          'salesLatestPeriod',
          'salesLatestPeriod',
          { id: 'N/A', name: 'N/A', regionId: 'N/A', salesLatestPeriod: 'N/A', salesMomentumShort: 'N/A', salesMomentumLong: 'N/A' }
        );

        centerTbX.topX.forEach(center => {
          center.salesLatestPeriod = isNaN(+center.salesLatestPeriod) ? 'N/A' : Math.round(+center.salesLatestPeriod);
          center.salesMomentumShort = isNaN(+center.salesMomentumShort) ? 'N/A' : Math.round(+center.salesMomentumShort) + '%';
          center.salesMomentumLong = isNaN(+center.salesMomentumLong) ? 'N/A' : Math.round(+center.salesMomentumLong) + '%';
        });
        centerTbX.btmX.forEach(center => {
          center.salesLatestPeriod = isNaN(+center.salesLatestPeriod) ? 'N/A' : Math.round(+center.salesLatestPeriod);
          center.salesMomentumShort = isNaN(+center.salesMomentumShort) ? 'N/A' : Math.round(+center.salesMomentumShort) + '%';
          center.salesMomentumLong = isNaN(+center.salesMomentumLong) ? 'N/A' : Math.round(+center.salesMomentumLong) + '%';
        });

        // build result
        result = {
          ...result,
          accountPlus: centerTbX.topX.map(topX => ({ account: topX.name, region: this.getRegionName(topX.regionId), total: topX.salesLatestPeriod, fourMonth: topX.salesMomentumShort, py: topX.salesMomentumLong })),
          accountMinus: centerTbX.btmX.map(btmX => ({ account: btmX.name, region: this.getRegionName(btmX.regionId), total: btmX.salesLatestPeriod, fourMonth: btmX.salesMomentumShort, py: btmX.salesMomentumLong })),
        };



        // check if showBricks
        const allBricksValues = result.brickPlus.map(item => item.total)
        .concat(result.brickPlus.map(item => item.fourMonth))
        .concat(result.brickPlus.map(item => item.py))
        .concat(result.brickMinus.map(item => item.total))
        .concat(result.brickMinus.map(item => item.fourMonth))
        .concat(result.brickMinus.map(item => item.py));
        const allBricksSum = addSmart(...allBricksValues);
        if (allBricksSum === 0 || allBricksSum === '0%') this.showBricks = false;
        // check if showAccounts
        const allAccountsValues = result.accountPlus.map(item => item.total)
        .concat(result.accountPlus.map(item => item.fourMonth))
        .concat(result.accountPlus.map(item => item.py))
        .concat(result.accountMinus.map(item => item.total))
        .concat(result.accountMinus.map(item => item.fourMonth))
        .concat(result.accountMinus.map(item => item.py));
        const allAccountsSum = addSmart(...allAccountsValues);
        if (allAccountsSum === 0 || allAccountsSum === '0%') this.showAccounts = false;

        return of(result);
      }),
    )
  }

  splitBricks<T>(bricks: T[], mShort: string, mLong: string, sales: string, sortBy: string, emptyTemplate?: Partial<T>): TopAndBottomX<T> {
    // Filter out bricks that have 0 or 0% in all three properties (mShort, mLong, sales)
    const filteredBricks = bricks.filter(brick => {
      const mShortValue = this.getNumber((brick as any)[mShort]);
      const mLongValue = this.getNumber((brick as any)[mLong]);
      const salesValue = this.getNumber((brick as any)[sales]);

      // Keep brick if ANY of the values is not 0
      return mShortValue !== 0 || mLongValue !== 0 || salesValue !== 0;
    });

    // Split bricks based on mShort value
    const topX: T[] = [];
    const btmX: T[] = [];

    filteredBricks.forEach(brick => {
      const mShortValue = this.getNumber((brick as any)[mShort]);

      if (mShortValue >= 0) {
        topX.push(brick);
      } else {
        btmX.push(brick);
      }
    });

    // Sort both arrays by sortBy property in descending order
    const sortFunction = (a: T, b: T): number => {
      const aValue = this.getNumber((a as any)[sortBy]);
      const bValue = this.getNumber((b as any)[sortBy]);
      return bValue - aValue; // descending order
    };

    topX.sort(sortFunction);
    btmX.sort(sortFunction);

    // Make arrays equal length by padding the shorter one with empty values
    if (emptyTemplate && topX.length !== btmX.length) {
      const diff = Math.abs(topX.length - btmX.length);
      const emptyItem = { ...emptyTemplate } as T;

      if (topX.length < btmX.length) {
        for (let i = 0; i < diff; i++) {
          topX.push({ ...emptyItem });
        }
      } else {
        for (let i = 0; i < diff; i++) {
          btmX.push({ ...emptyItem });
        }
      }
    }

    return { topX, btmX };
  }

  getRegionName(id: string): string {
    return this.regions.find(region => region.id === id)?.name || id;
  }

  getNumber(val: number | string): number {
    if (typeof val === 'number') return val;
    if (typeof val === 'string') {
      let str = val.trim();
      if (str.endsWith('%')) {
        str = str.slice(0, -1).replace(',', '.');
        const num = parseFloat(str);
        return isNaN(num) ? 0 : num;
      }
      if (/^-?\d+(,\d+)?$/.test(str)) {
        const num = parseFloat(str.replace(',', '.'));
        return isNaN(num) ? 0 : num;
      }
      // plain number
      const num = parseFloat(str);
      return isNaN(num) ? 0 : num;
    }
    return 0;
  };

}
